import { useEffect, useRef, useState } from "react";

import type { LoomControlSelectionIntent } from "./LoomControlSelectionBridge";
import type { LoomProductSelection } from "./LoomProductTree";
import type { LoomAuthoringDocument } from "./loomAuthoringModel";

export function useLoomProductSelection(document: LoomAuthoringDocument, projectId: string,
  runtimeControlSurfaceId: string | null) {
  const [selectedNodeId, setSelectedNodeId] = useState("wing");
  const [selection, setSelection] = useState<LoomProductSelection>({ kind: "node", nodeId: "wing" });
  const [intent, setIntent] = useState<LoomControlSelectionIntent | null>(null);
  const previousDocument = useRef(document);
  const intentSequence = useRef(0);
  const selectedExists = document.nodes.some(({ id }) => id === selectedNodeId);
  const removed = selectedExists ? null : previousDocument.current.nodes.find(({ id }) => id === selectedNodeId);
  const fallbackIds = [
    ...(removed?.type === "transform" ? [removed.inputNodeId] : []),
    ...document.rootNodeIds, ...document.nodes.map(({ id }) => id),
  ];
  const effectiveNodeId = selectedExists ? selectedNodeId
    : fallbackIds.find((id) => document.nodes.some((node) => node.id === id)) ?? "";
  const productSelection: LoomProductSelection = selection.nodeId === effectiveNodeId
    ? selection : { kind: "node", nodeId: effectiveNodeId };
  const node = document.nodes.find(({ id }) => id === effectiveNodeId);
  const runtimeControlExists = node?.type === "primitive" && node.definition.primitive === "wing"
    && node.definition.controlSurfaces?.controlSurfaces.some(({ id }) => id === runtimeControlSurfaceId);

  const selectNode = (nodeId: string) => {
    setSelectedNodeId(nodeId); setSelection({ kind: "node", nodeId });
    const target = document.nodes.find(({ id }) => id === nodeId);
    setIntent(target?.type === "primitive" && target.definition.primitive === "wing"
      ? { kind: "node", nodeId, requestId: ++intentSequence.current } : null);
  };
  const selectControl = (nodeId: string, controlSurfaceId: string) => {
    setSelectedNodeId(nodeId); setSelection({ kind: "control", nodeId, controlSurfaceId });
    setIntent({ kind: "control", nodeId, controlSurfaceId,
      requestId: ++intentSequence.current });
  };
  const selectSection = (nodeId: string, sectionId: string) => {
    setSelectedNodeId(nodeId); setSelection({ kind: "section", nodeId, sectionId }); setIntent(null);
  };
  const settleIntent = (requestId: number) => setIntent((current) => (
    current?.requestId === requestId ? null : current
  ));

  useEffect(() => {
    previousDocument.current = document;
    if (effectiveNodeId === selectedNodeId) return;
    setSelectedNodeId(effectiveNodeId); setSelection({ kind: "node", nodeId: effectiveNodeId });
  }, [document, effectiveNodeId, selectedNodeId]);
  useEffect(() => {
    setSelection((current) => {
      if (current.kind === "section") return current;
      const matchingControlIntent = intent?.kind === "control" && intent.nodeId === current.nodeId
        && current.kind === "control" && intent.controlSurfaceId === current.controlSurfaceId;
      if (!runtimeControlSurfaceId && current.kind === "control") {
        return matchingControlIntent ? current : { kind: "node", nodeId: current.nodeId };
      }
      if (runtimeControlSurfaceId && runtimeControlExists && current.nodeId === effectiveNodeId
          && (current.kind === "node" || current.controlSurfaceId !== runtimeControlSurfaceId)) {
        if (intent?.kind === "node" && intent.nodeId === current.nodeId) return current;
        return { kind: "control", nodeId: current.nodeId,
          controlSurfaceId: runtimeControlSurfaceId };
      }
      return current;
    });
  }, [effectiveNodeId, intent, runtimeControlExists, runtimeControlSurfaceId]);
  useEffect(() => {
    setSelectedNodeId("wing"); setSelection({ kind: "node", nodeId: "wing" }); setIntent(null);
  }, [projectId]);

  return { controlSelectionIntent: intent, effectiveSelectedNodeId: effectiveNodeId,
    productSelection, selectControl, selectNode, selectSection, settleControlSelectionIntent: settleIntent };
}
